import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TesterImportPreview, TesterImportStatus } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel, StateChip } from '../../components/atoms.js';
import { mapApiError } from '../../lib/errors.js';
import { testerImportApi } from '../../lib/tester-import-api.js';

// #131 — Tester per Excel. Vorlage → ausfüllen → hochladen → Vorschau → übernehmen. Zeilen mit
// Fehlern sperren das Übernehmen (Offener Punkt 4): lieber die Datei korrigieren als einen Teil
// still liegen lassen. Übersprungene Zeilen (nicht im Plan-Scope) sperren nicht — sie werden
// gemeldet und nicht angefasst.
const TONE: Record<TesterImportStatus, 'default' | 'ok' | 'warn' | 'fail'> = {
  assign: 'ok',
  unchanged: 'default',
  skipped: 'warn',
  error: 'fail',
};

export function PlanTesterImport({
  projectId,
  planId,
  planName,
  planClosed,
}: {
  projectId: string;
  planId: string;
  planName: string;
  planClosed: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<TesterImportPreview | null>(null);
  const [alsoDefault, setAlsoDefault] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Zuweisungen, die der Server beim Übernehmen nicht mehr durchgelassen hat (Testfall
  // zwischenzeitlich aus dem Scope genommen) — still verschluckt wäre das ein Scheinerfolg.
  const [skippedOnApply, setSkippedOnApply] = useState(0);

  const upload = useMutation({
    mutationFn: (file: File) => testerImportApi.preview(projectId, planId, file),
    meta: { errorToast: false },
    // Eine alte Vorschau darf nicht übernehmbar bleiben, während eine neue Datei geladen wird
    // oder fehlschlägt.
    onMutate: () => {
      setError(null);
      setResult(null);
      setSkippedOnApply(0);
    },
    onSuccess: setResult,
    onError: (err) => setError(mapApiError(t, err)),
  });
  const apply = useMutation({
    mutationFn: () =>
      testerImportApi.apply(projectId, planId, {
        assignments: (result?.rows ?? [])
          .filter((r) => r.status === 'assign' && r.test_case_id && r.new_tester)
          .map((r) => ({
            test_case_id: r.test_case_id as string,
            tester_id: r.new_tester?.user_id as string,
          })),
        also_default: alsoDefault,
      }),
    meta: { errorToast: false, successToast: 'plans.testerImport.applied' },
    onMutate: () => setError(null),
    onSuccess: (res) => {
      setResult(null);
      setSkippedOnApply(res.skipped.length);
      void queryClient.invalidateQueries({ queryKey: ['plan', projectId, planId] });
      // „auch als Standard-Tester merken" schreibt in den Katalog — dessen Anzeige muss mitziehen.
      if (alsoDefault) {
        void queryClient.invalidateQueries({ queryKey: ['catalog-testcases', projectId] });
        // #143: Standard-Tester ist Filterkriterium im Katalogbaum (Volliste).
        void queryClient.invalidateQueries({ queryKey: ['tcs-search', projectId] });
      }
    },
    onError: (err) => setError(mapApiError(t, err)),
  });

  const blocked = !result || result.counts.error > 0 || result.counts.assign === 0;

  return (
    <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <h2 className="text-sm font-bold text-text-primary">{t('plans.testerImport.title')}</h2>
      <p className="mt-1 text-[11px] text-text-muted">{t('plans.testerImport.hint')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() =>
            void testerImportApi
              .downloadTemplate(projectId, planId, planName)
              .catch((err) => setError(mapApiError(t, err)))
          }
          className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body"
        >
          {t('plans.testerImport.download')}
        </button>
        {planClosed ? (
          <span className="text-[11px] text-text-muted">{t('plans.testerImport.closed')}</span>
        ) : (
          <label className="text-sm text-text-body">
            {t('plans.testerImport.upload')}
            <input
              type="file"
              accept=".xlsx"
              disabled={upload.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
              className="ml-2 text-xs"
            />
          </label>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-status-fail">
          {error}
        </p>
      )}
      {skippedOnApply > 0 && (
        <p className="mt-2 text-xs text-status-warn">
          {t('plans.testerImport.applySkipped', { count: skippedOnApply })}
        </p>
      )}
      {result && !planClosed && (
        <div className="mt-3">
          <p className="text-xs text-text-body">{t('plans.testerImport.counts', result.counts)}</p>
          {/* Eine ETO-Datei hat Hunderte Zeilen — die Tabelle scrollt in sich, damit „Übernehmen"
              auf dem 1280×800-Schirm ohne Scrollen der ganzen Seite erreichbar bleibt. */}
          <div className="mt-2 max-h-72 overflow-auto rounded-btn border border-[var(--border)]">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-bg-surface text-left text-text-muted">
                <tr>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.row')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.test')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.title')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.current')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.new')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.status')}</th>
                  <th className="px-2 py-1 font-medium">{t('plans.testerImport.col.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.row_no} className="border-t border-[var(--border)]">
                    <td className="px-2 py-1 text-text-muted">{r.row_no}</td>
                    <td className="px-2 py-1">
                      {r.human_id ? <IdLabel id={r.human_id} /> : r.test_number}
                    </td>
                    <td className="px-2 py-1 text-text-body">{r.title ?? ''}</td>
                    <td className="px-2 py-1 text-text-muted">{r.current_tester ?? '—'}</td>
                    <td className="px-2 py-1 text-text-body">
                      {r.new_tester ? r.new_tester.name : r.tester_raw}
                      {r.new_tester?.is_stub && (
                        <>
                          {' '}
                          <StateChip label={t('plans.testerImport.stub')} />
                        </>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <StateChip
                        label={t(`plans.testerImport.status.${r.status}`)}
                        tone={TONE[r.status]}
                      />
                    </td>
                    <td className="px-2 py-1 text-text-muted">
                      {r.reason ? t(`plans.testerImport.reason.${r.reason}`) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs text-text-body">
            <input
              type="checkbox"
              aria-label={t('plans.testerImport.alsoDefault')}
              checked={alsoDefault}
              onChange={(e) => setAlsoDefault(e.target.checked)}
            />
            {t('plans.testerImport.alsoDefault')}
          </label>
          <button
            type="button"
            disabled={blocked || apply.isPending}
            onClick={() => apply.mutate()}
            className="mt-3 rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {t('plans.testerImport.apply')}
          </button>
          {result.counts.error > 0 && (
            <p className="mt-1 text-[11px] text-status-fail">{t('plans.testerImport.fixErrors')}</p>
          )}
        </div>
      )}
    </section>
  );
}
